import { captureException } from "@sentry/react";

type ReportableError = {
  error: Error;
  /** Grouping for errors whose stack trace says nothing about where they came from. */
  fingerprint?: string[];
};

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function stringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Turns a thrown non-Error (e.g. better-auth's `{ code, message, status, statusText }`) into an
 * Error so Sentry titles the issue with its code and message rather than
 * "Object captured as exception with keys: …".
 *
 * Such an Error is created here, so its stack is the same for every caller; the returned
 * fingerprint groups it by code and status instead.
 */
export function toReportableError(thrown: unknown): ReportableError {
  if (thrown instanceof Error) {
    return { error: thrown };
  }

  if (typeof thrown !== "object" || thrown === null) {
    const error = new Error(`Non-error thrown: ${stringify(thrown)}`, { cause: thrown });
    error.name = "NonErrorException";
    return { error, fingerprint: ["non-error-exception", typeof thrown] };
  }

  const status =
    "status" in thrown && typeof thrown.status === "number" ? thrown.status : undefined;
  const code = "code" in thrown ? nonEmptyString(thrown.code) : undefined;
  const message =
    ("message" in thrown ? nonEmptyString(thrown.message) : undefined) ??
    ("statusText" in thrown ? nonEmptyString(thrown.statusText) : undefined);

  const summary = [status !== undefined ? `HTTP ${status}` : undefined, code]
    .filter(Boolean)
    .join(" ");
  const text = [summary, message].filter(Boolean).join(": ") || stringify(thrown);

  const error = new Error(text, { cause: thrown });
  error.name = status !== undefined ? "ResponseError" : "NonErrorException";
  return {
    error,
    fingerprint: [error.name, code ?? message ?? "", status !== undefined ? String(status) : ""],
  };
}

/**
 * Reports any thrown value, converting non-Errors as `toReportableError` does. `key` names the
 * caller so its non-Error failures don't group with another caller's of the same code and status.
 */
export function reportToSentry(thrown: unknown, key: readonly string[] = []): void {
  const { error, fingerprint } = toReportableError(thrown);
  captureException(error, fingerprint ? { fingerprint: [...key, ...fingerprint] } : undefined);
}

/**
 * React Query `meta` that decides whether the app-wide query/mutation error handler reports a
 * failure to Sentry. Without it, every failure is reported.
 */
export type SentryReportMeta = { shouldReportToSentry: (error: unknown) => boolean };

export function shouldReportToSentry(
  meta: Record<string, unknown> | undefined,
  error: unknown,
): boolean {
  const predicate = meta?.shouldReportToSentry;
  return typeof predicate === "function" ? Boolean(predicate(error)) : true;
}

// better-auth errors carry `status`; hooks that rethrow them as an Error keep the original as `cause`.
function responseStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }
  if ("status" in error && typeof error.status === "number") {
    return error.status;
  }
  return error instanceof Error ? responseStatus(error.cause) : undefined;
}

/** Skips 4xx responses, which for better-auth are the user's own mistakes (e.g. a wrong password). */
export const SKIP_SENTRY_FOR_CLIENT_ERRORS: SentryReportMeta = {
  shouldReportToSentry: (error) => {
    const status = responseStatus(error);
    return status === undefined || status >= 500;
  },
};

/** For hooks that report their own failures. */
export const SKIP_SENTRY: SentryReportMeta = { shouldReportToSentry: () => false };
